import React, { useEffect, useState } from "react";
import { collection, getDocs, doc, updateDoc } from "firebase/firestore";
import { getFirestore } from "firebase/firestore";
import { serverFetch } from "../utils/serverApi";

// users.createdAt is stored in several shapes depending on the signup path:
// Firestore Timestamp, {seconds}, ISO string, or epoch millis.
const toDate = (v) => {
  if (!v) return null;
  if (typeof v.toDate === "function") return v.toDate();
  if (typeof v.seconds === "number") return new Date(v.seconds * 1000);
  const d = new Date(v);
  return isNaN(d.getTime()) ? null : d;
};

const formatDate = (v) => {
  const d = toDate(v);
  return d
    ? d.toLocaleString("en-GB", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" })
    : "N/A";
};

const UsersPage = () => {
  const [users, setUsers] = useState([]);
  const [editedCoins, setEditedCoins] = useState({});
  const [loading, setLoading] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [verifyingId, setVerifyingId] = useState(null);

  const db = getFirestore(); // Initialize Firestore

  useEffect(() => {
    const fetchUsers = async () => {
      const usersCollection = collection(db, "users");
      const usersSnapshot = await getDocs(usersCollection);
      const usersList = usersSnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
      // Newest registrations first; users with no date go last.
      usersList.sort((a, b) => (toDate(b.createdAt)?.getTime() ?? 0) - (toDate(a.createdAt)?.getTime() ?? 0));
      setUsers(usersList);
    };

    fetchUsers();
  }, [db]);

  const handleCoinInputChange = (id, value) => {
    setEditedCoins((prev) => ({ ...prev, [id]: value }));
  };

  const handleSaveCoins = async (id) => {
    setLoading(true);
    setAlertMessage("");
    try {
      const newCoins = editedCoins[id];
      const userDoc = doc(db, "users", id);
      await updateDoc(userDoc, { coins: newCoins });
      setUsers((prevUsers) =>
        prevUsers.map((user) =>
          user.id === id ? { ...user, coins: newCoins } : user
        )
      );
      setEditedCoins((prev) => {
        const updated = { ...prev };
        delete updated[id];
        return updated;
      });
      setAlertMessage("Coins updated successfully!");
    } catch (error) {
      console.error("Error updating coins: ", error);
      setAlertMessage("Failed to update coins. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // For a user whose verification code never arrived: verifies the account so they can simply log in.
  const handleVerifyEmail = async (user) => {
    if (!window.confirm(`Verify ${user.email || "this user"} without a code? They will be able to log in straight away.`)) return;
    setVerifyingId(user.id);
    setAlertMessage("");
    try {
      await serverFetch("/api/admin/users/verify-email", { method: "POST", body: { uid: user.id } });
      setUsers((prev) => prev.map((u) => (u.id === user.id ? { ...u, emailVerified: true } : u)));
      setAlertMessage(`Email verified successfully for ${user.email}. They can log in now.`);
    } catch (error) {
      setAlertMessage(error?.message || "Could not verify this user. Please try again.");
    } finally {
      setVerifyingId(null);
    }
  };

  // Filter users based on search term
  const filteredUsers = users.filter((user) =>
    Object.values(user)
      .filter((value) => typeof value === "string")
      .some((value) => value.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="max-w-5xl mx-auto p-6 bg-white rounded-lg shadow-lg mt-8">
      <h1 className="text-2xl font-bold text-gray-800 mb-6 text-center">
        Users
      </h1>
      {alertMessage && (
        <div
          className={`p-4 mb-6 rounded-md text-sm ${
            alertMessage.includes("successfully")
              ? "bg-green-100 text-green-700"
              : "bg-red-100 text-red-700"
          }`}
        >
          {alertMessage}
        </div>
      )}
      <div className="mb-6">
        <input
          type="text"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          placeholder="Search users by name, email, school, course, or phone..."
          className="w-full px-4 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
        />
      </div>
      {users.length === 0 ? (
        <p className="text-gray-600 text-center">No users found.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full table-auto border-collapse">
            <thead>
              <tr className="bg-blue-800 text-white">
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Name
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Email
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Verified
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  School
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Course
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Phone Number
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Date Registered
                </th>
                <th className="px-4 py-3 text-left text-sm font-medium">
                  Coins
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user, index) => (
                <tr
                  key={user.id}
                  className={`${
                    index % 2 === 0 ? "bg-gray-50" : "bg-white"
                  } hover:bg-gray-100 transition duration-200`}
                >
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {user.fullName || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {user.email || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {user.emailVerified === true ? (
                      <span className="text-green-700 font-medium">Verified</span>
                    ) : (
                      <div className="flex items-center space-x-2">
                        <span className="text-gray-500">Not verified</span>
                        <button
                          onClick={() => handleVerifyEmail(user)}
                          disabled={verifyingId === user.id || !user.email}
                          className="px-3 py-1 rounded-md text-white text-xs bg-green-600 hover:bg-green-700 disabled:bg-gray-400 disabled:cursor-not-allowed"
                        >
                          {verifyingId === user.id ? "Verifying..." : "Verify"}
                        </button>
                      </div>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {user.school || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {user.course || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700">
                    {user.phone || "N/A"}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-700 whitespace-nowrap">
                    {formatDate(user.createdAt)}
                  </td>
                  <td className="px-4 py-3 text-sm">
                    <div className="flex items-center space-x-2">
                      <input
                        type="number"
                        value={(editedCoins[user.id] ?? user.coins) || 0}
                        onChange={(e) =>
                          handleCoinInputChange(
                            user.id,
                            parseInt(e.target.value, 10)
                          )
                        }
                        className="w-24 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                        min="0"
                      />
                      <button
                        onClick={() => handleSaveCoins(user.id)}
                        disabled={loading}
                        className={`px-4 py-2 rounded-md text-white ${
                          loading
                            ? "bg-gray-400 cursor-not-allowed"
                            : "bg-blue-600 hover:bg-blue-700"
                        } transition duration-200`}
                      >
                        {loading ? "Saving..." : "Save"}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UsersPage;
